import { useEffect, useState } from "react";
import { toast } from "sonner";
import Field, { describedBy } from "@/components/Field";
import FormDialog from "@/components/FormDialog";
import PasswordInput from "@/components/PasswordInput";
import NewPasswordFields, { EMPTY_NEW_PASSWORD, isValidNewPassword } from "./NewPasswordFields";
import { changePassword } from "../utils";
import { useSession } from "../session/SessionContext";

interface Props {
  open: boolean;
  onClose(): void;
}

const ChangePasswordModal: React.FC<Props> = ({ open, onClose }) => {
  const { replaceToken } = useSession();
  const [currentPassword, setCurrentPassword] = useState("");
  const [value, setValue] = useState(EMPTY_NEW_PASSWORD);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setCurrentPassword("");
      setValue(EMPTY_NEW_PASSWORD);
      setSubmitted(false);
      setError(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    setSubmitted(true);
    if (!currentPassword || !isValidNewPassword(value)) return;
    setSubmitting(true);
    setError(null);
    try {
      const { token } = await changePassword(currentPassword, value.password);
      replaceToken(token);
      toast.success("Wachtwoord gewijzigd. Je andere sessies zijn uitgelogd.");
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const currentError = submitted && !currentPassword ? "Vul je huidige wachtwoord in" : null;

  return (
    <FormDialog
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title="Wachtwoord wijzigen"
      description="Je andere sessies worden daarna uitgelogd."
      submitLabel="Wachtwoord wijzigen"
      submitting={submitting}
      error={error}
      onSubmit={handleSubmit}
    >
      <Field id="current-password" label="Huidig wachtwoord" error={currentError}>
        <PasswordInput
          id="current-password"
          autoComplete="current-password"
          value={currentPassword}
          aria-invalid={currentError != null}
          aria-describedby={describedBy("current-password")}
          onChange={(e) => setCurrentPassword(e.target.value)}
        />
      </Field>
      <NewPasswordFields value={value} onChange={setValue} showErrors={submitted} />
    </FormDialog>
  );
};

export default ChangePasswordModal;
